/* Copyright (c) 2025 MOCA Systems, Inc. All rights reserved. */

import { DestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
import { FancyGraphics } from '@appCanvasCommon/graphics/FancyGraphics';
import { ComponentInterface } from '@appCanvasCommon/interfaces/component.interface';
import { TicketContainer, TicketLayoutMode } from '@appPlanApplication/tickets/TicketContainer';
import { TicketUtils } from '@appCanvasCommon/utils/TicketUtils';
import { Side, TicketType } from '@touchplan/common';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { LayerPaths } from '@appPlanApplication/LayerPaths';

const LEFT_SIDEBAR_MENU_WIDTH = 84;

export interface StickyLabelConfiguration {
	marginLeft?: number;
}

export class StickyContentContainer extends FancyContainer implements ComponentInterface {
	private childComponent: ComponentInterface;
	private maskedContent: FancyContainer;
	private maskArea: FancyGraphics = new FancyGraphics();

	constructor(childComponent: ComponentInterface, maskedContent: FancyContainer) {
		super();
		this.childComponent = childComponent;
		this.name = 'StickyContentContainer';
		this.maskedContent = maskedContent;

		this.addChild(this.maskedContent);
	}

	private applyMask(width: number, height: number): void {
		const hasMaskArea = this.children.includes(this.maskArea);
		// Skip if the mask area is already added and matches the requested size
		if (hasMaskArea && this.maskArea.width === width && this.maskArea.height === height) {
			return;
		}
		// If the mask is already added, just update its dimensions
		if (hasMaskArea) {
			this.maskArea.width = width;
			this.maskArea.height = height;
			return;
		}
		// Otherwise, create and apply a new mask with the specified dimensions
		this.maskArea.clear();
		this.maskArea.beginFill(0xff0000, 1);
		this.maskArea.drawRect(0, 0, width, height);
		this.maskArea.endFill();
		this.addChild(this.maskArea);
		this.mask = this.maskArea;
	}

	private removeMask(): void {
		if (!this.children.includes(this.maskArea)) {
			return;
		}
		this.removeChild(this.maskArea);
		this.mask = null;
	}

	public update = (
		canvasAngularLand: CanvasAngularLand,
		ticketContainer: TicketContainer,
		forceDirty: boolean = false
	): void => {
		if (!ticketContainer.visible || !ticketContainer.renderable) {
			return;
		}
		// Broadcast the update event to the child component
		if (this.childComponent?.update) {
			this.childComponent.update(canvasAngularLand, ticketContainer, forceDirty);
		}

		const ticketData = ticketContainer.getData();
		const ticketLayout = ticketContainer.getLayout();
		const ticketType = TicketUtils.getTicketType(ticketData);

		// Apply the sticky effect only to Task tickets that are on the ACTIVE side.
		if (!(ticketType === TicketType.Task) || !(ticketData.view.side === Side.ACTIVE)) {
			this.removeMask();
			return;
		}

		const scale = ticketContainer.camera.getScale();
		const layoutOffsetLeft = LEFT_SIDEBAR_MENU_WIDTH / scale._x;

		const cameraXOffset =
			ticketLayout.currentLayer === LayerPaths.TICKETS
				? ticketContainer.camera.globalView.x - ticketLayout.left + layoutOffsetLeft
				: (ticketLayout.left - LEFT_SIDEBAR_MENU_WIDTH) * -1;

		// If the left edge of the ticket is obscured by the side menu,
		// shift the text to the right to create a "sticky" effect.
		// Otherwise, reset the position and remove the mask.
		if (cameraXOffset > 0) {
			this.applyMask(ticketLayout.width, ticketLayout.height);
			this.maskedContent.x = cameraXOffset;
		} else {
			this.removeMask();
			this.maskedContent.x = 0;
		}
	};

	public override destroy(options?: DestroyOptions): void {
		this.removeMask();
		super.destroy(options);
	}
}
